import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, AlertTriangle, WifiOff, Plus, Trash2 } from 'lucide-react';
import { User } from '../types';
import { fetchBookings, Booking, isLifecycleConnected } from '../utils/lifecycleApi';
import { loadFromStorage } from '../utils/helpers';

const CONTENT_STATUSES = ['Not Started','In Progress','Completed & Approved','Delivered','On Hold','YTD'];
const SOCIAL_STATUSES = ['Not Applicable','Pending','Scheduled','Delivered & Posted','Posted','Awaiting Customer Action'];
const OVERALL_STATUSES = ['Not Started','In Progress','Completed','Delivered','Posted'];
const CATEGORIES = ['Reel','Teaser','Highlight','Full Video','Insta Slides','Photo','Short Film','Other'];

const STATUS_COLORS: Record<string,string> = {
  'Not Started':'#6B7280','In Progress':'#1D4ED8','Completed & Approved':'#059669',
  'Delivered':'#065F46','On Hold':'#B45309','YTD':'#7C3AED',
  'Not Applicable':'#9CA3AF','Pending':'#B45309','Scheduled':'#1D4ED8',
  'Delivered & Posted':'#059669','Posted':'#065F46','Awaiting Customer Action':'#DC2626',
  'Completed':'#059669','Posted_overall':'#065F46',
};

const PP_QUOTE_MAP: Record<string,{category:string,title:string}> = {
  'Full Event Video (per hr) Single Cam': {category:'Full Video',title:'Full Event Video (Single Cam)'},
  'Full Event Video (per hr) Double Cam': {category:'Full Video',title:'Full Event Video (Double Cam)'},
  'Teaser + Highlight': {category:'Teaser',title:'Teaser'},
  'Wedding Teaser': {category:'Teaser',title:'Wedding Teaser'},
  'Instagram Reel': {category:'Reel',title:'Instagram Reel'},
  'Short Film (~45 mins)': {category:'Short Film',title:'Short Film'},
  'Cinematography (Scripted)': {category:'Full Video',title:'Scripted Cinematic Video'},
  'Digital Wedding Invitation': {category:'Other',title:'Digital Wedding Invitation'},
};

const STORAGE_KEY = 'ss_pp_tracker_v2';
interface PPItem { id:string; category:string; title:string; contentStatus:string; socialStatus:string; comments:string; deliveryDate:string; overallStatus:string; vendor:string; }

function loadPP(): Record<number,PPItem[]> { try { return JSON.parse(localStorage.getItem(STORAGE_KEY)||'{}'); } catch { return {}; } }
function savePP(d:any) { localStorage.setItem(STORAGE_KEY,JSON.stringify(d)); }

export default function PostProductionTracker({ user }: { user: User }) {
  const [connected] = useState(isLifecycleConnected());
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState<Booking|null>(null);
  const [ppData, setPPData] = useState<Record<number,PPItem[]>>(loadPP());
  const [newItem, setNewItem] = useState({category:'Reel',title:'',vendor:''});

  const load = useCallback(async () => {
    if (!connected) { setLoading(false); return; }
    setLoading(true);
    try { setBookings(await fetchBookings()); } catch(e:any) { setError(e.message); } finally { setLoading(false); }
  }, [connected]);

  useEffect(() => { load(); }, [load]);

  const getItems = (bookingId:number): PPItem[] => ppData[bookingId] || [];

  const autoPopulate = (b:Booking) => {
    if (ppData[b.id]?.length > 0) return;
    let links:any={}; try { links=JSON.parse(localStorage.getItem('ss_booking_quote_links')||'{}'); } catch {}
    const linked = links[b.id];
    if (!linked) return;
    const quotes = loadFromStorage<any[]>('ss_quotes',[]);
    const quote = quotes.find((q:any)=>q.quoteNumber===linked?.quoteNumber);
    if (!quote) return;
    const pkg = (quote.packageItems||[])[0];
    const ppVendors:any[] = pkg?.ppVendors||[];
    const vendorName = ppVendors[0]?.vendor||'';
    const items:PPItem[] = [];
    const seen = new Set<string>();
    ppVendors.forEach((v:any)=>{
      const mapped = PP_QUOTE_MAP[v.deliverable];
      if (mapped && !seen.has(mapped.title)) {
        seen.add(mapped.title);
        items.push({id:Date.now()+Math.random()+'',category:mapped.category,title:mapped.title,vendor:vendorName,contentStatus:'Not Started',socialStatus:'Not Applicable',comments:`Rate: ₹${v.rate}${(v.qty||1)>1?` × ${v.qty}`:''}`,deliveryDate:'',overallStatus:'Not Started'});
      }
      // Auto-add Highlight if Teaser was added
      if (v.deliverable==='Teaser + Highlight' && !seen.has('Highlight')) {
        seen.add('Highlight');
        items.push({id:Date.now()+Math.random()+'',category:'Highlight',title:'Highlight',vendor:vendorName,contentStatus:'Not Started',socialStatus:'Not Applicable',comments:'',deliveryDate:'',overallStatus:'Not Started'});
      }
    });
    if (items.length > 0) { const u={...ppData,[b.id]:items}; setPPData(u); savePP(u); }
  };

  const updateItem = (bookingId:number, itemId:string, field:keyof PPItem, value:string) => {
    const items = getItems(bookingId).map(i=>i.id===itemId?{...i,[field]:value}:i);
    const u={...ppData,[bookingId]:items}; setPPData(u); savePP(u);
  };

  const addItem = (bookingId:number) => {
    if (!newItem.title.trim()) return;
    const item:PPItem = {id:Date.now()+'',category:newItem.category,title:newItem.title,vendor:newItem.vendor,contentStatus:'Not Started',socialStatus:'Not Applicable',comments:'',deliveryDate:'',overallStatus:'Not Started'};
    const u={...ppData,[bookingId]:[...getItems(bookingId),item]};
    setPPData(u); savePP(u); setNewItem({category:'Reel',title:'',vendor:''});
  };

  const removeItem = (bookingId:number, itemId:string) => {
    const u={...ppData,[bookingId]:getItems(bookingId).filter(i=>i.id!==itemId)};
    setPPData(u); savePP(u);
  };

  if (!connected) return <div className="page-content"><div className="alert alert-error"><WifiOff size={15}/> Not connected.</div></div>;

  return (
    <div className="page-content">
      <div className="page-header">
        <div><h1>Post-Production Tracker</h1><p style={{color:'var(--text-secondary)',fontSize:13}}>Per-function tracking — Content Status + Social Media Status per deliverable</p></div>
        <button className="btn btn-ghost btn-icon" onClick={load}><RefreshCw size={15}/></button>
      </div>
      {error && <div className="alert alert-error" style={{marginBottom:16}}><AlertTriangle size={15}/> <span>{error}</span></div>}

      <div style={{display:'grid',gridTemplateColumns:'260px 1fr',gap:20}}>
        <div>
          <label style={{fontSize:12,marginBottom:8,display:'block'}}>Select Booking</label>
          {loading?<div style={{color:'var(--text-muted)',fontSize:13}}>Loading…</div>:bookings.map(b=>{
            const items=getItems(b.id);
            const done=items.filter(i=>i.overallStatus==='Delivered'||i.overallStatus==='Posted').length;
            return (
              <div key={b.id} onClick={()=>{setSelected(b);autoPopulate(b);}}
                style={{padding:'10px 14px',borderRadius:8,marginBottom:6,cursor:'pointer',
                  border:`1.5px solid ${selected?.id===b.id?'var(--teal)':'var(--border)'}`,
                  background:selected?.id===b.id?'rgba(56,178,172,0.06)':'#fff'}}>
                <div style={{fontWeight:600,fontSize:13}}>{b.customer_name}</div>
                <div style={{fontSize:11,color:'var(--text-muted)'}}>{b.csr_number} · {b.service_type}</div>
                {items.length>0&&<div style={{fontSize:11,color:'var(--teal)',marginTop:2}}>{done}/{items.length} completed</div>}
              </div>
            );
          })}
        </div>

        <div>
          {!selected?(
            <div className="card" style={{textAlign:'center',padding:40,color:'var(--text-muted)'}}>Select a booking to track PP deliverables</div>
          ):(
            <>
              <div className="card" style={{marginBottom:14,padding:'12px 16px'}}>
                <div style={{fontWeight:700,fontSize:14}}>{selected.customer_name} — {selected.csr_number}</div>
                <div style={{fontSize:12,color:'var(--text-muted)'}}>
                  {getItems(selected.id).filter(i=>i.overallStatus==='Delivered'||i.overallStatus==='Posted').length}/{getItems(selected.id).length} deliverables complete
                </div>
              </div>

              {/* Items table */}
              <div className="card" style={{marginBottom:14,padding:0,overflowX:'auto'}}>
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                  <thead>
                    <tr style={{background:'var(--bg-subtle)',borderBottom:'2px solid var(--border)'}}>
                      {['Category','Title','Vendor','Content Status','Social Media Status','Delivery Date','Overall',''].map(h=>(
                        <th key={h} style={{padding:'8px 10px',textAlign:'left',fontWeight:600,color:'var(--text-muted)',fontSize:11,whiteSpace:'nowrap'}}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {getItems(selected.id).map(item=>(
                      <tr key={item.id} style={{borderBottom:'1px solid var(--border)'}}>
                        <td style={{padding:'6px 10px'}}>
                          <span style={{fontSize:10,fontWeight:600,padding:'2px 8px',borderRadius:100,background:'var(--bg-subtle)',color:'var(--text-secondary)'}}>{item.category}</span>
                        </td>
                        <td style={{padding:'6px 10px'}}>
                          <input style={{width:120,padding:'3px 6px',border:'1px solid var(--border)',borderRadius:4,fontSize:12,fontFamily:'inherit'}}
                            value={item.title} onChange={e=>updateItem(selected.id,item.id,'title',e.target.value)}/>
                          {item.comments&&<div style={{fontSize:10,color:'var(--text-muted)',marginTop:2}}>{item.comments}</div>}
                        </td>
                        <td style={{padding:'6px 10px'}}>
                          <input style={{width:80,padding:'3px 6px',border:'1px solid var(--border)',borderRadius:4,fontSize:12,fontFamily:'inherit'}}
                            value={item.vendor} onChange={e=>updateItem(selected.id,item.id,'vendor',e.target.value)}/>
                        </td>
                        <td style={{padding:'6px 10px'}}>
                          <select style={{padding:'3px 6px',border:'1px solid var(--border)',borderRadius:4,fontSize:11,color:STATUS_COLORS[item.contentStatus]||'#000',fontFamily:'inherit'}}
                            value={item.contentStatus} onChange={e=>updateItem(selected.id,item.id,'contentStatus',e.target.value)}>
                            {CONTENT_STATUSES.map(s=><option key={s} style={{color:STATUS_COLORS[s]}}>{s}</option>)}
                          </select>
                        </td>
                        <td style={{padding:'6px 10px'}}>
                          <select style={{padding:'3px 6px',border:'1px solid var(--border)',borderRadius:4,fontSize:11,color:STATUS_COLORS[item.socialStatus]||'#000',fontFamily:'inherit'}}
                            value={item.socialStatus} onChange={e=>updateItem(selected.id,item.id,'socialStatus',e.target.value)}>
                            {SOCIAL_STATUSES.map(s=><option key={s} style={{color:STATUS_COLORS[s]}}>{s}</option>)}
                          </select>
                        </td>
                        <td style={{padding:'6px 10px'}}>
                          <input type="date" style={{padding:'3px 6px',border:'1px solid var(--border)',borderRadius:4,fontSize:11,fontFamily:'inherit'}}
                            value={item.deliveryDate} onChange={e=>updateItem(selected.id,item.id,'deliveryDate',e.target.value)}/>
                        </td>
                        <td style={{padding:'6px 10px'}}>
                          <select style={{padding:'3px 6px',border:'1px solid var(--border)',borderRadius:4,fontSize:11,color:STATUS_COLORS[item.overallStatus]||'#000',fontFamily:'inherit',fontWeight:600}}
                            value={item.overallStatus} onChange={e=>updateItem(selected.id,item.id,'overallStatus',e.target.value)}>
                            {OVERALL_STATUSES.map(s=><option key={s}>{s}</option>)}
                          </select>
                        </td>
                        <td style={{padding:'6px 10px'}}>
                          <button onClick={()=>removeItem(selected.id,item.id)} className="btn btn-ghost btn-icon btn-sm"><Trash2 size={12} color="var(--red-light)"/></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {getItems(selected.id).length===0&&<div style={{padding:24,textAlign:'center',color:'var(--text-muted)',fontSize:13}}>No items yet. Add below or link a quote to auto-populate.</div>}
              </div>

              {/* Add item */}
              <div className="card">
                <div style={{fontWeight:600,fontSize:13,marginBottom:10}}>+ Add Deliverable</div>
                <div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'flex-end'}}>
                  <div>
                    <div style={{fontSize:11,marginBottom:4}}>Category</div>
                    <select className="input" style={{padding:'6px 8px',fontSize:12}} value={newItem.category} onChange={e=>setNewItem({...newItem,category:e.target.value})}>
                      {CATEGORIES.map(c=><option key={c}>{c}</option>)}
                    </select>
                  </div>
                  <div style={{flex:1,minWidth:140}}>
                    <div style={{fontSize:11,marginBottom:4}}>Title (e.g. Mehendi Reel, Haldi Reel)</div>
                    <input className="input" style={{padding:'6px 8px',fontSize:12}} placeholder="e.g. Sangeet Reel" value={newItem.title} onChange={e=>setNewItem({...newItem,title:e.target.value})}
                      onKeyDown={e=>e.key==='Enter'&&addItem(selected.id)}/>
                  </div>
                  <div style={{width:100}}>
                    <div style={{fontSize:11,marginBottom:4}}>Vendor</div>
                    <input className="input" style={{padding:'6px 8px',fontSize:12}} placeholder="Gunabh" value={newItem.vendor} onChange={e=>setNewItem({...newItem,vendor:e.target.value})}/>
                  </div>
                  <button className="btn btn-primary" onClick={()=>addItem(selected.id)} style={{padding:'6px 16px',fontSize:12}}>
                    <Plus size={13}/> Add
                  </button>
                </div>
                <div style={{fontSize:11,color:'var(--text-muted)',marginTop:8}}>Changes save automatically · Add each function separately (e.g. Mehendi Reel, Haldi Reel, Sangeet Reel)</div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
